import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || '')
  .split(',').map(e => e.trim()).filter(Boolean);

const Login = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('credentials'); // 'credentials' | 'totp'
  const [form, setForm] = useState({ email: '', password: '', totp: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [splash, setSplash] = useState(false);

  const isAdmin = ADMIN_EMAILS.includes(form.email.trim().toLowerCase()) ||
                  ADMIN_EMAILS.includes(form.email.trim());

  const enterWithSplash = (destination) => {
    setSplash(true);
    setTimeout(() => navigate(destination), 1400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isAdmin) {
        const payload = { password: form.password };
        if (step === 'totp') payload.totp_code = form.totp;

        const res = await axios.post(`${BACKEND_URL}/api/admin/login`, payload);

        if (res.data.status === 'totp_required') {
          setStep('totp');
          setLoading(false);
          return;
        }

        sessionStorage.setItem('admin_token', res.data.token);
        sessionStorage.setItem('admin_email', form.email);
        enterWithSplash('/admin');
      } else {
        const res = await axios.post(`${BACKEND_URL}/api/students/login`, {
          email: form.email,
          password: form.password,
        });
        localStorage.setItem('student_token', res.data.token);
        localStorage.setItem('student_user', JSON.stringify(res.data.student));
        enterWithSplash(res.data.student.mustChangePassword ? '/trocar-senha' : '/area');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'E-mail ou senha incorretos.');
      setLoading(false);
    }
  };

  if (splash) {
    return (
      <div className="fixed inset-0 bg-[#0A0A0A] flex items-center justify-center z-50">
        <div className="relative flex items-center justify-center">
          {/* Anel girando */}
          <div className="absolute w-24 h-24 rounded-full border-2 border-transparent border-t-white/60 border-r-white/20 animate-spin" />
          {/* Logo centralizada */}
          <img src="/images/logo-symbol.png" alt="Daniel Aron" className="w-14 h-auto opacity-90" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6 pt-20 lg:pt-0">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold tracking-tight uppercase mb-2">Daniel Aron</h1>
          <p className="text-[#A3A3A3] text-sm tracking-widest uppercase">
            {step === 'totp' ? 'Verificação em dois fatores' : 'Acesso à plataforma'}
          </p>
        </div>

        <div className="bg-[#121212] border border-white/10 p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 text-red-400 text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {step === 'credentials' && (
              <>
                <div>
                  <label htmlFor="login-email" className="block text-xs text-[#A3A3A3] uppercase tracking-widest mb-2">E-mail</label>
                  <input
                    id="login-email"
                    type="email" required autoFocus
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none text-sm transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="login-password" className="block text-xs text-[#A3A3A3] uppercase tracking-widest mb-2">Senha</label>
                  <input
                    id="login-password"
                    type="password" required
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none text-sm transition-colors"
                  />
                </div>
              </>
            )}

            {step === 'totp' && (
              <div>
                <label htmlFor="login-totp" className="block text-xs text-[#A3A3A3] uppercase tracking-widest mb-2">Código do autenticador</label>
                <input
                  id="login-totp"
                  type="text" required autoFocus inputMode="numeric" maxLength={6}
                  value={form.totp}
                  onChange={e => {
                    const totp = e.target.value.replace(/\D/g, '');
                    const updated = { ...form, totp };
                    setForm(updated);
                    if (totp.length === 6) {
                      setError('');
                      setLoading(true);
                      axios.post(`${BACKEND_URL}/api/admin/login`, { password: updated.password, totp_code: totp })
                        .then(res => {
                          sessionStorage.setItem('admin_token', res.data.token);
                          sessionStorage.setItem('admin_email', updated.email);
                          enterWithSplash('/admin');
                        })
                        .catch(err => {
                          setError(err.response?.data?.detail || 'Código inválido.');
                          setForm(f => ({ ...f, totp: '' }));
                          setLoading(false);
                        });
                    }
                  }}
                  placeholder="000000"
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 focus:border-white/40 outline-none text-sm transition-colors text-center tracking-widest text-lg"
                />
                <button type="button" onClick={() => { setStep('credentials'); setError(''); }}
                  className="mt-3 text-xs text-[#555] hover:text-white transition-colors">
                  ← Voltar
                </button>
              </div>
            )}

            <button
              type="submit" disabled={loading}
              className="w-full py-3 bg-white text-black font-bold uppercase tracking-widest text-sm hover:bg-[#E5E5E5] transition-colors mt-2 disabled:opacity-50"
            >
              {loading ? 'Entrando...' : step === 'totp' ? 'Verificar' : 'Entrar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
